import { useRef } from 'react'
import { useRequest } from 'ahooks'

import { Table } from '@sar/component'
import { message } from 'antd'

import { UploadImage, UploadFile } from 'components'
import { ModalForm, ProFormTextArea } from '@ant-design/pro-components'

async function fetchData (params, store) {
  const result = await store.externals.api.get(`/orders`, {
    params: Object.assign(params, {
      page: params.current,
      size: params.pageSize,
    })
  })

  return {
    success: true,
    data: result.data,
    total: result.totals,
  }
}

function Create ({ data, store, visible, onVisibleChange }) {
  const fileType = React.useMemo(() => store.config.getConstantPairs('FileType'), [])

  const getFormData = React.useCallback(data => {
    let without = [
        'afterPicture'
      , 'afterVideo'
    ]

    return Object.assign(R.omit(without, data), {
      fileManageList: [
        ...R.defaultTo([], data.afterVideo).map(file => ({ ...file, type: fileType.afterVideo, fileName: file.filename })),
        ...R.defaultTo([], data.afterPicture).map(file => ({ ...file, type: fileType.afterPicture, fileName: file.filename })),
      ]
    })
  }, [])

  return <ModalForm
    title='新增售后记录'
    layout='vertical'
    open={visible}
    modalProps={{
      centered: true,
      maskClosable: false,
      destroyOnClose: true
    }}
    onOpenChange={onVisibleChange}
    onFinish={params => store.externals.api.post(`/afterSales/${data.id}`, getFormData(params)).then(() => [ message.success('新增售后记录成功，可前往售后列表查看') ]).then(R.T)}
  >
    <UploadFile
      max={10}
      name='afterVideo'
      label='售后视频'
      help='建议尺寸800*800px'
      fieldProps={{ upload: file => store.oss.client.multipartUpload(`${store.externals.nonce()}.${file.name.split('.').pop()}`, file).then(res => `//${store.oss.endpoint}/${res.name}`) }}
    />

    <UploadImage
      max={10000}
      name='afterPicture'
      label='售后图片'
      fieldProps={{ upload: file => store.oss.client.multipartUpload(`${store.externals.nonce()}.${file.name.split('.').pop()}`, file).then(res => `//${store.oss.endpoint}/${res.name}`) }}
    />

    <ProFormTextArea
      name='describes'
      label='问题描述'
      fieldProps={{ rows: 6 }}
      rules={[ { required: true, message: '请输入问题描述' } ]}
    />
  </ModalForm>
}

function Remark ({ data, store, visible, handles, onVisibleChange }) {
  return <ModalForm
    title='订单备注'
    layout='vertical'
    open={visible}
    modalProps={{
      centered: true,
      maskClosable: false,
      destroyOnClose: true
    }}
    onOpenChange={onVisibleChange}
    request={async () => data}
    onFinish={params => store.externals.api.put(`/orders/remark/${data.id}`, params).then(() => handles.current.reload()).then(R.T)}
  >
    <ProFormTextArea
      name='remark'
      label='订单备注'
      fieldProps={{ rows: 6 }}
      rules={[ { required: true, message: '请输入订单备注' } ]}
    />
  </ModalForm>
}

export default function ({ store, navigator }) {
  const res = useRequest<any, any>(() => store.externals.api.get(`/statistics`))

  const columns = [
    {
      title: '订单编号',
      dataIndex: 'number',
    },
    {
      title: '客户姓名',
      dataIndex: 'customerName',
    },
    {
      title: '生产负责人',
      dataIndex: 'producerName',
      hideInTable: true,
    },
    {
      title: '联系电话',
      dataIndex: 'customerPhone',
      render: _ => _.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2'),
    },
    {
      title: '地址',
      dataIndex: 'customerAddress',
    },
    {
      title: '来源',
      dataIndex: 'sourceId',
      valueType: 'treeSelect',
      hideInTable: true,
      fieldProps: { fieldNames: { value: 'id', label: 'source' } },
      request: () => store.externals.api.get(`/constant/source`),
    },
    {
      title: '信息来源',
      dataIndex: 'customerSource',
    },
    {
      title: '总金额',
      dataIndex: 'totalAmount',
    },
    {
      title: '已付款',
      dataIndex: 'paidAmount',
    },
    {
      title: '待付款',
      dataIndex: 'balance',
      render: (_, data) => R.defaultTo(0, data.totalAmount) - R.defaultTo(0, data.paidAmount),
    },
    {
      title: '跟踪员',
      dataIndex: 'saleName',
    },
    {
      title: '生产类型',
      dataIndex: 'type',
      valueType: 'select',
      valueEnum: store.config.getConstantEnums(`OrderType`),
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      valueType: 'select',
      valueEnum: store.config.getConstantEnums(`OrderStatus`),
    },
  ]

  const actions = [
    {
      name: '新增',
      type: 'normal',
      onClick: () => navigator.to(`/order/list/create`, `新增订单`),
      visible: () => store.identity.user.roles?.findIndex(role => [ 1, 26 ].includes(role.id)) != -1,
    },
  ]

  const rowActions = [
    {
      name: '详情',
      visible: () => store.identity.hasPermission('/order/list:detail'),
      onClick: (data, handles) => navigator.to(`/order/list/detail/${data.id}`, `查看订单（${data.customerName}）`),
    },
    {
      name: '新增售后',
      form: props => <Create {...props} store={store} />,
      visible: () => store.identity.hasPermission('/order/list:aftsales'),
    },
    {
      name: '订单备注',
      form: props => <Remark {...props} store={store} />,
      visible: () => store.identity.hasPermission('/order/list:remark'),
    },
    {
      name: '删除',
      danger: true,
      confirmType: 'modal',
      confirmMessage: '您确定删除这个订单吗？',
      visible: () => store.identity.hasPermission('/order/list:delete'),
      onConfirm: (data, handles) => store.externals.api.delete(`/orders/list/${data.id}`).then(() => handles.current.reload()),
    },
  ]

  const cols = React.useMemo(() => {
    let showSearch = [
        'customerName'
      , 'customerPhone'
      , 'sourceId'
      , 'customerAddress'
      , 'producerName'
      , 'type'
      , 'saleName'
      , 'status'
    ]

    return columns.map(column => {
      if (showSearch.includes(column.dataIndex)) {
        return Object.assign(column)
      }

      else {
        return Object.assign(column, { hideInSearch: true })
      }
    })
  }, [])

  const fields = React.useMemo(() => [
    { title: '共查询到订单', key: 'totalOrder' },
    { title: '原木订单', key: 'logOrder' },
    { title: '板式订单', key: 'plateOrder' },
    { title: '今日新增订单', key: 'todayOrder' },
    { title: '设计中订单', key: 'design' },
    { title: '沟通报价', key: 'quotation' },
    { title: '拆单中', key: 'splitOrder' },
    { title: '下单生产', key: 'purchase' },
    { title: '生产中', key: 'production' },
    { title: '装配中', key: 'assembling' },
    { title: '订单完结', key: 'end' },
  ], [])

  const onRow = React.useCallback(data => {
    let ignore = [
        'assembling'
      , 'end'
    ]

    if (ignore.includes(data.status)) {
      return null
    }

    else if (data.warningTime == null) {
      return null
    }

    else if (moment().isBefore(data.warningTime, 'day')) {
      return null
    }

    else {
      return {
        className: 'text-red-600'
      }
    }
  }, [])

  return <div className='p-4'>
    <div className='flex flex-wrap'>
      {fields.map(field => {
        return <div className='mr-4 mb-4'>【{field.title}】<span className='text-orange-600 font-bold mr-2'>{res.data?.[field.key]}</span>单</div>
      })}
    </div>

    <Table
      onRow={onRow}
      scroll={{ }}
      title='订单列表'
      columns={cols}
      actions={actions}
      rowActions={rowActions}
      request={params => fetchData(params, store)}
      search={{ defaultCollapsed: false, span: store.config.responsive }}
    />
  </div>
}
